import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { askWheel } from "../lib/api";
import { toast } from "sonner";

const SOLO_SUGGESTIONS = [
  "Will I find love this year?",
  "Should I switch jobs?",
  "What am I supposed to learn right now?",
  "Is this a good time to move cities?",
  "What's quietly blocking me?",
  "When does my money luck shift?",
];

const PAIR_SUGGESTIONS = [
  "Where do we get each other wrong?",
  "What does conflict look like for us?",
  "Is this pair built to last?",
  "How do we make money decisions together?",
  "What season tests us the most?",
  "What quietly holds us together?",
];

export const AskWheel = ({ context, thread, onThreadChange, readOnly = false, askFn, mode = "solo" }) => {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const call = askFn || askWheel;

  const handleAsk = async (questionText) => {
    const question = (questionText ?? q).trim();
    if (question.length < 3 || loading) return;
    setLoading(true);
    setQ("");
    try {
      // Pass the cosmic profile (for consistency with the main almanac) and the
      // last 5 Q&A turns from this thread (for continuity inside the chat).
      const recent_qa = thread.slice(0, 5).map((t) => ({
        question: t.question,
        answer: t.answer,
        insights: (t.insights || []).slice(0, 8),
      }));
      const data = await call({ ...context, question, recent_qa });
      onThreadChange?.([{ question, answer: data.answer, insights: data.insights }, ...thread]);
    } catch (e) {
      console.error(e);
      toast.error("The wheel went quiet. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const showInput = !readOnly;

  return (
    <section data-testid="ask-wheel-section" className="mt-20 sm:mt-24 max-w-3xl mx-auto">
      <div className="text-center mb-8 sm:mb-10">
        <p className="text-stardust/70 uppercase tracking-[0.4em] sm:tracking-[0.5em] text-[10px] mb-4 sm:mb-5">
          {readOnly ? "What they asked the wheel" : "Ask the wheel"}
        </p>
        <h3 className="font-display text-3xl sm:text-5xl text-[#F5EBD8] mb-3 sm:mb-4">
          {readOnly ? "Questions, answered." : (<>What do you wish to know<span className="text-stardust">?</span></>)}
        </h3>
        {!readOnly && (
          <p className="text-slate-400 max-w-xl mx-auto leading-relaxed text-sm sm:text-base px-2">
            {mode === "pair"
              ? "Anything about the two of you. Timing, tension, why one of you goes quiet in October. Astrarium answers through the pair chart."
              : "Anything goes. Love, work, family, timing, the call you keep putting off. Astrarium answers through your chart."}
          </p>
        )}
      </div>

      {showInput && (
        <div className="relative">
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-2 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            <Textarea
              data-testid="ask-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Will I find love in the coming year?"
              disabled={loading}
              rows={3}
              className="resize-none bg-transparent border-none text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:ring-offset-0 text-base px-3 sm:px-4 pt-3 pb-14 sm:pb-12"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAsk();
              }}
            />
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
              <span className="text-white/30 text-[11px] hidden sm:block">⌘ + Enter</span>
              <Button
                data-testid="ask-submit"
                onClick={() => handleAsk()}
                disabled={loading || q.trim().length < 3}
                className="ml-auto bg-stardust text-cosmic-900 hover:bg-stardust-light hover:shadow-[0_0_24px_rgba(226,194,117,0.45)] disabled:opacity-40 rounded-lg px-5 py-2 h-auto font-medium uppercase tracking-[0.25em] text-[11px] transition-all"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    Ask
                    <Send className="h-3.5 w-3.5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showInput && thread.length === 0 && !loading && (
        <div className="mt-6 flex flex-wrap gap-2 justify-center" data-testid="ask-suggestions">
          {(mode === "pair" ? PAIR_SUGGESTIONS : SOLO_SUGGESTIONS).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handleAsk(s)}
              className="text-xs text-slate-300 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-stardust/50 hover:bg-stardust/5 hover:text-stardust transition-all"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-10 text-center"
        >
          <Sparkles className="h-4 w-4 text-stardust mx-auto animate-pulse mb-2" />
          <p className="text-slate-400 italic font-serif-italic">The wheel is turning&hellip;</p>
        </motion.div>
      )}

      <div className="mt-8 sm:mt-10 space-y-6 sm:space-y-8">
        <AnimatePresence initial={false}>
          {thread.map((item, idx) => (
            <motion.div
              key={`${idx}-${item.question.slice(0, 12)}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-8"
              data-testid={`ask-thread-${idx}`}
            >
              <p className="text-stardust/70 uppercase tracking-[0.3em] text-[10px] mb-2">
                {readOnly ? "They asked" : "You asked"}
              </p>
              <p className="font-serif-italic text-lg sm:text-xl text-white/95 mb-5 sm:mb-6">{item.question}</p>

              <div className="h-px bg-gradient-to-r from-transparent via-stardust/30 to-transparent my-5 sm:my-6" />

              <p className="text-stardust/70 uppercase tracking-[0.3em] text-[10px] mb-3">The wheel answers</p>
              <p className="text-slate-200 leading-relaxed text-[15px] sm:text-lg mb-4 sm:mb-5">{item.answer}</p>

              {item.insights?.length > 0 && (
                <ul className="space-y-2 mt-4">
                  {item.insights.map((line, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-400 text-sm leading-relaxed">
                      <span className="text-stardust mt-0.5 shrink-0">✦</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};
